/** @jsxImportSource @emotion/react */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useAppDispatch, useAppSelector } from 'src/redux';

import { Box, Stack, Typography } from '@mui/material';
import { CustomButton, CustomPagination, TextButton } from 'src/components';
import { EmploymentBenefits } from './components/EmploymentBenefits';
import { ReferralBenefits } from './components/ReferralBenefits';
import { ProfessionalBenefits } from './components/ProfessionalBenefits';

import {
  confirmIntermediateScreen,
  setApplicationCancellationReason,
  userSelectors,
} from 'src/redux/user';
import {
  getEmploymentBenefitsByLocation,
  getProfessionalBenefitsByLocation,
  getReferralBenefitsByLocation,
} from 'src/utils';
import {
  APP_ROUTES,
  CompensationBenefitsOrder,
  CompensationBenefitType,
  CancelApplicationReason,
} from 'src/constants';
import { useNavigate } from 'react-router-dom';

export const AEBenefitsPage: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const userData = useAppSelector(userSelectors.getUserData);
  const confirmIntermediateScreenLoading = useAppSelector(
    userSelectors.getIsUserPerformingAction,
  );

  const [currentBenefits, setCurrentBenefits] = useState(0);

  const benefitsData = {
    employmentBenefits: getEmploymentBenefitsByLocation(
      userData?.country || null,
    ),
    referralBenefits: getReferralBenefitsByLocation(userData?.country || null),
    professionalBenefits: getProfessionalBenefitsByLocation(
      userData?.country || null,
    ),
  };

  useEffect(() => {
    if (Object.values(benefitsData).every((e) => !e)) {
      dispatch(confirmIntermediateScreen());
    }
  }, [benefitsData]);

  const availableBenefits = CompensationBenefitsOrder.filter(
    (benefitType) =>
      (benefitType === CompensationBenefitType.Employment &&
        !!benefitsData.employmentBenefits) ||
      (benefitType === CompensationBenefitType.Referral &&
        !!benefitsData.referralBenefits) ||
      (benefitType === CompensationBenefitType.Professional &&
        !!benefitsData.professionalBenefits),
  );

  const isLastBenefits = useMemo(
    () => currentBenefits === availableBenefits.length - 1,
    [currentBenefits, availableBenefits],
  );
  const handleClickNext = useCallback(() => {
    if (isLastBenefits) {
      dispatch(confirmIntermediateScreen());
    } else {
      setCurrentBenefits((currBenefits) => currBenefits + 1);
    }
  }, [isLastBenefits]);

  const currentBenefitType = availableBenefits[currentBenefits];

  if (
    !benefitsData.employmentBenefits &&
    !benefitsData.referralBenefits &&
    !benefitsData.professionalBenefits
  )
    return null;

  const onCancelClick = useCallback(() => {
    dispatch(
      setApplicationCancellationReason(
        CancelApplicationReason.DONT_LIKE_ENGAGEMENT_MODEL,
      ),
    );
    navigate(`/${APP_ROUTES.CANCEL_APPLICATION}`);
  }, []);

  return (
    <Box
      sx={(theme) => ({
        background: theme.palette.highlight.neutral,
        padding: { xs: '32px 20px', md: '48px 72px' },
        borderRadius: { xs: '24px', md: '48px' },
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'start',
        gap: '24px',
        marginTop: { md: '-3rem' },
      })}
    >
      <Stack spacing={1} direction="row" alignItems="center">
        <CustomPagination
          amount={availableBenefits.length}
          selectedPage={currentBenefits}
          onSelectPage={(selectedPage) => setCurrentBenefits(selectedPage)}
        />
        <Typography variant="body2" color="text.purple">
          Working with AgileEngine
        </Typography>
      </Stack>

      {currentBenefitType === CompensationBenefitType.Employment && (
        <EmploymentBenefits data={benefitsData.employmentBenefits} />
      )}
      {currentBenefitType === CompensationBenefitType.Referral && (
        <ReferralBenefits data={benefitsData.referralBenefits} />
      )}
      {currentBenefitType === CompensationBenefitType.Professional && (
        <ProfessionalBenefits data={benefitsData.professionalBenefits} />
      )}

      <Stack
        spacing={4}
        direction={{ xs: 'column', md: 'row' }}
        alignItems={{ xs: 'center', md: 'start' }}
        alignSelf={{ xs: 'center', md: 'start' }}
      >
        <CustomButton
          onClick={handleClickNext}
          label={isLastBenefits ? 'Continue' : 'Next'}
          isLoading={confirmIntermediateScreenLoading}
        />
        <Stack direction="column" alignItems={{ xs: 'center', md: 'start' }}>
          <Typography variant="body2" color="secondary" textAlign="center">
            Don't like the engagement model?
          </Typography>
          <TextButton
            variant="secondary"
            underlined={true}
            onClick={onCancelClick}
          >
            Cancel job application
          </TextButton>
        </Stack>
      </Stack>
    </Box>
  );
};
